import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClientSignIn } from "../axios/api.js";

// Private notes about the logged-in user — visible only to them.
export const fetchMyMistakes = createAsyncThunk(
  "mistakes/fetchMy",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/mistakes/my");
      return response.data.notes || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load notes.");
    }
  }
);

// Notes the logged-in manager has reported — visible only to them.
export const fetchReportedByMe = createAsyncThunk(
  "mistakes/fetchReportedByMe",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/mistakes/reported-by-me");
      return response.data.notes || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load reported notes.");
    }
  }
);

// Colleagues a manager can report a mistake about. This hits the same
// "who's in my department" endpoint the Achievements feature uses — it's a
// generic department-colleagues lookup, not achievement-specific.
export const fetchReportableColleagues = createAsyncThunk(
  "mistakes/fetchReportableColleagues",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/achievements/colleagues");
      return response.data.users || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load colleagues.");
    }
  }
);

export const reportMistake = createAsyncThunk(
  "mistakes/report",
  async ({ recipientId, title, description, severity }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClientSignIn.post("/mistakes/report", {
        recipientId, title, description, severity,
      });
      dispatch(fetchReportedByMe()); // so "Reported by Me" reflects it immediately
      return response.data.note;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to log the note.");
    }
  }
);

const mistakeSlice = createSlice({
  name: "mistakes",
  initialState: {
    myMistakes: [],
    myMistakesLoading: false,

    reportedByMe: [],
    reportedByMeLoading: false,

    colleagues: [],
    colleaguesLoading: false,

    reporting: false,
    reportError: null,
  },
  reducers: {
    clearReportError: (state) => {
      state.reportError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyMistakes.pending, (state) => {
        state.myMistakesLoading = true;
      })
      .addCase(fetchMyMistakes.fulfilled, (state, action) => {
        state.myMistakesLoading = false;
        state.myMistakes = action.payload;
      })
      .addCase(fetchMyMistakes.rejected, (state) => {
        state.myMistakesLoading = false;
      })

      .addCase(fetchReportedByMe.pending, (state) => {
        state.reportedByMeLoading = true;
      })
      .addCase(fetchReportedByMe.fulfilled, (state, action) => {
        state.reportedByMeLoading = false;
        state.reportedByMe = action.payload;
      })
      .addCase(fetchReportedByMe.rejected, (state) => {
        state.reportedByMeLoading = false;
      })

      .addCase(fetchReportableColleagues.pending, (state) => {
        state.colleaguesLoading = true;
      })
      .addCase(fetchReportableColleagues.fulfilled, (state, action) => {
        state.colleaguesLoading = false;
        state.colleagues = action.payload;
      })
      .addCase(fetchReportableColleagues.rejected, (state) => {
        state.colleaguesLoading = false;
      })

      .addCase(reportMistake.pending, (state) => {
        state.reporting = true;
        state.reportError = null;
      })
      .addCase(reportMistake.fulfilled, (state) => {
        state.reporting = false;
      })
      .addCase(reportMistake.rejected, (state, action) => {
        state.reporting = false;
        state.reportError = action.payload;
      });
  },
});

export const { clearReportError } = mistakeSlice.actions;
export default mistakeSlice.reducer;
